import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./Answer.css";
import "./AnswerTenPointSlider.css";
import "normalize.css";
import stronglyAgree from "../../assets/strongly_agree.gif";
import agree from "../../assets/agree.gif";
import neutral from "../../assets/neutral.gif";
import sad from "../../assets/sad.gif";
import stronglySad from "../../assets/strongly_sad.gif";

const mapValuesOfEmojies = {
  1: stronglySad,
  2: sad,
  3: neutral,
  4: agree,
  5: stronglyAgree,
};

function toIntAnswerCode(answerCode) {
  const n = Number.parseInt(String(answerCode ?? "").trim(), 10);
  return Number.isNaN(n) ? null : n;
}

function fallbackFiveEmojiForTen(value) {
  const mapped = Math.round(((value - 1) / 9) * 4) + 1;
  return Math.min(5, Math.max(1, mapped));
}

/** 10-point UI + bubble: 1–4 stronglySad, 5 neutral, 6–10 stronglyAgree (same 3 GIFs). */
function resolveTenPointThreeTierGif(codeOrValue) {
  const n =
    typeof codeOrValue === "number"
      ? codeOrValue
      : toIntAnswerCode(codeOrValue);
  if (n === null || n < 1) return mapValuesOfEmojies[3];
  if (n <= 4) return mapValuesOfEmojies[1];
  if (n === 5) return mapValuesOfEmojies[3];
  if (n <= 10) return mapValuesOfEmojies[5];
  return mapValuesOfEmojies[3];
}

/** Likert image: optional 10-point three-tier; else 5-point map + legacy 1–10→5 spread. */
function resolveLTypeEmojiImgSrc(answerCode, useTenPointThreeTier = false) {
  const c = toIntAnswerCode(answerCode);
  if (c === null) return mapValuesOfEmojies[3];
  if (useTenPointThreeTier) return resolveTenPointThreeTierGif(c);
  return (
    mapValuesOfEmojies[c] ||
    (c >= 1 && c <= 10 ? mapValuesOfEmojies[fallbackFiveEmojiForTen(c)] : undefined) ||
    mapValuesOfEmojies[3]
  );
}

function TenPointOutlineSad() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.65" />
      <circle cx="8.5" cy="10" r="1" fill="currentColor" />
      <circle cx="15.5" cy="10" r="1" fill="currentColor" />
      <path
        d="M8.5 15.5C10 13.6 14 13.6 15.5 15.5"
        stroke="currentColor"
        strokeWidth="1.65"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

function TenPointOutlineHappy() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.65" />
      <circle cx="8.5" cy="9.5" r="1" fill="currentColor" />
      <circle cx="15.5" cy="9.5" r="1" fill="currentColor" />
      <path
        d="M8.5 14.2C10 16.2 14 16.2 15.5 14.2"
        stroke="currentColor"
        strokeWidth="1.65"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

function TenPointLikertSlider({
  answer,
  qid,
  gid,
  type,
  onAnswerClick,
  initialAnswerCode,
  onCancel,
}) {
  const byCode = useMemo(() => {
    const m = new Map();
    answer.forEach((ans) => {
      const c = toIntAnswerCode(ans?.answer_code);
      if (c !== null) m.set(c, ans);
    });
    return m;
  }, [answer]);

  const seedValue = useMemo(() => {
    const fromSaved = toIntAnswerCode(initialAnswerCode);
    if (fromSaved !== null && byCode.has(fromSaved)) return fromSaved;
    if (byCode.has(5)) return 5;
    const keys = [...byCode.keys()].sort((a, b) => a - b);
    return keys.length ? keys[Math.floor(keys.length / 2)] : 5;
  }, [byCode, initialAnswerCode]);

  const [value, setValue] = useState(seedValue);
  const valueRef = useRef(value);
  useEffect(() => {
    setValue(seedValue);
  }, [seedValue]);
  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  const commit = useCallback(
    (v) => {
      const ans = byCode.get(v);
      if (ans) onAnswerClick(ans, qid, gid, type);
    },
    [byCode, onAnswerClick, qid, gid, type]
  );

  const handlePointerUp = useCallback(() => {
    commit(valueRef.current);
  }, [commit]);

  const handleKeyUp = useCallback(
    (e) => {
      if (
        e.key === "ArrowLeft" ||
        e.key === "ArrowRight" ||
        e.key === "Home" ||
        e.key === "End" ||
        e.key === "PageUp" ||
        e.key === "PageDown"
      ) {
        commit(Number(e.currentTarget.value));
      }
    },
    [commit]
  );

  const popoverLeftPct = ((value - 1) / 9) * 100;

  return (
    <div className="ten-point-likert" role="group" aria-label="Rating 1 to 10">
      {onCancel ? (
        <button
          type="button"
          className="ten-point-likert__close"
          aria-label="Cancel and keep previous rating"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onCancel();
          }}
        >
          ×
        </button>
      ) : null}
      <div className="ten-point-likert__slider-block">
        <div className="ten-point-likert__track-stack">
          <div className="ten-point-likert__popover-rail" aria-hidden="true">
            <div
              className="ten-point-likert__popover"
              style={{ left: `${popoverLeftPct}%` }}
            >
              <div className="ten-point-likert__popover-inner">
                <img
                  className="ten-point-likert__popover-emoji"
                  src={resolveTenPointThreeTierGif(value)}
                  alt=""
                />
                <div className="ten-point-likert__popover-rating">
                  <span className="ten-point-likert__popover-rating-label">Rating:</span>
                  <span className="ten-point-likert__popover-rating-num">{value}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="ten-point-likert__range-wrap">
            <input
              type="range"
              className="ten-point-likert__range"
              min={1}
              max={10}
              step={1}
              value={value}
              aria-valuemin={1}
              aria-valuemax={10}
              aria-valuenow={value}
              aria-valuetext={`${value} of 10`}
              onChange={(e) => setValue(Number(e.target.value))}
              onPointerUp={handlePointerUp}
              onKeyUp={handleKeyUp}
            />
          </div>
          <div className="ten-point-likert__scale-row" aria-hidden="true">
            <span className="ten-point-likert__face-icon">
              <TenPointOutlineSad />
            </span>
            <div className="ten-point-likert__scale-dots">
              {[2, 3, 4, 5, 6, 7, 8, 9].map((i) => (
                <span key={i} className="ten-point-likert__dot" />
              ))}
            </div>
            <span className="ten-point-likert__face-icon">
              <TenPointOutlineHappy />
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Answer(props) {
  const {
    answer,
    onAnswerClick,
    qid,
    gid,
    type,
    isComment,
    isStart,
    customLTypeQuestion,
    initialLikertAnswerCode,
    onTenPointSliderCancel,
    isSubmit,
    /** When `answer` is a single givenAnswer object, pass question.answer.length so 10-point tier GIFs apply. */
    likertOptionCount,
  } = props;
  const isSubmitQuestion = Boolean(isSubmit);
  const shouldShowEmojiForLType =
    type === "L" && Boolean(customLTypeQuestion) && !isSubmitQuestion;
  const shouldRenderButtonList =
    isSubmitQuestion ||
    isStart ||
    (type === "L" ? !customLTypeQuestion : customLTypeQuestion);
  const scaleCount =
    (Array.isArray(customLTypeQuestion?.answer) &&
      customLTypeQuestion.answer.length) ||
    (Array.isArray(answer) && answer.length) ||
    (typeof likertOptionCount === "number" && likertOptionCount >= 2
      ? likertOptionCount
      : 0) ||
    5;
  const useTenPointLikertCard =
    !isSubmitQuestion &&
    type === "L" &&
    scaleCount === 10 &&
    Array.isArray(answer) &&
    Boolean(onAnswerClick);
  const useTenPointThreeTierEmoji = scaleCount === 10;

  return (
    <>
      {Array.isArray(answer) ? (
        shouldRenderButtonList ? (
          <>
            <div className="startSurveyContainer">
              {answer.map((ans, index) => {
                return (
                  <a
                    key={index}
                    className="startBtn mobile:text-xs mobile:py-2 mobile:px-4 text-base px-4 py-3"
                    onClick={() => {
                      onAnswerClick(ans, qid, gid, type);
                    }}
                  >
                    {" "}
                    {ans.answer}
                  </a>
                );
              })}
            </div>
          </>
        ) : useTenPointLikertCard ? (
          <div
            className={
              type === "L" ? "mine replyMessagesEmojies" : "mine replyMessages"
            }
          >
            <div
              className={
                type === "L"
                  ? "replyMessagesEmojies replyMessagesEmojies--ten-wide"
                  : "replyMessages"
              }
            >
              <TenPointLikertSlider
                answer={answer}
                qid={qid}
                gid={gid}
                type={type}
                onAnswerClick={onAnswerClick}
                initialAnswerCode={initialLikertAnswerCode}
                onCancel={onTenPointSliderCancel}
              />
            </div>
          </div>
        ) : (
          <div
            className={
              type === "L" ? "mine replyMessagesEmojies" : "mine replyMessages"
            }
          >
            <div
              className={
                type === "L" ? "replyMessagesEmojies" : "replyMessages"
              }
            >
              {answer
                .slice()
                .reverse()
                .map((ans) => {
                  const code = toIntAnswerCode(ans?.answer_code);
                  return (
                    <a
                      key={ans.aid}
                      onClick={() => {
                        onAnswerClick(ans, qid, gid, type);
                      }}
                      className={type === "L" ? "btnEmoji" : "btn"}
                    >
                      {type === "L" ? (
                        <span className="emoji-tooltip-wrapper">
                          <img
                            className="emoji-options w-10 mobile:w-8"
                            src={resolveLTypeEmojiImgSrc(
                              ans?.answer_code,
                              useTenPointThreeTierEmoji
                            )}
                            alt={ans?.answer || "emoji option"}
                          />
                          <span className="emoji-tooltip-text">{ans?.answer}</span>
                        </span>
                      ) : (
                        ans?.answer
                      )}
                    </a>
                  );
                })}
            </div>
          </div>
        )
      ) : (
        <div
          className={
            shouldShowEmojiForLType
              ? "mine replyMessagesEmojies"
              : "mine replyMessages"
          }
        >
          <div
            className={
              shouldShowEmojiForLType
                ? "mine replyMessagesEmojies last"
                : "mine replyMessagesAnsweres last text-base mobile:text-xs font-medium py-4 px-6 mobile:py-3.5 mobile:px-4"
            }
          >
            {shouldShowEmojiForLType ? (
              <img
                className="w-10 mobile:w-8"
                src={resolveLTypeEmojiImgSrc(
                  answer?.answer_code,
                  useTenPointThreeTierEmoji
                )}
                alt={answer?.answer || "selected emoji"}
              />
            ) : isComment ? (
              answer?.comment
            ) : type === "L" ? (
              answer?.answer
            ) : (
              answer?.answer_code?.answer
            )}
          </div>
        </div>
      )}
    </>
  );
}
